import { typography } from "@memorang/helpers/font";
import deepmerge from "deepmerge";
import {
	DarkTheme as NavigationDarkTheme,
	DefaultTheme as NavigationDefaultTheme,
} from "expo-router/react-navigation";
import {
	MD3DarkTheme,
	MD3LightTheme,
	adaptNavigationTheme,
	configureFonts,
} from "react-native-paper";
import type { NavigationTheme } from "react-native-paper/lib/typescript/types";
import {
	breakpoints,
	buildComponentOverrides,
	buildFontConfig,
} from "./constants";
import { getCustomPalette, textColors } from "./palette";

export type BrandThemeInput = {
	themeColors?: {
		light?: Record<string, string>;
		dark?: Record<string, string>;
	};
	font?: string;
	darkMode: boolean;
	appComponentOverrides?: Record<string, unknown>;
	roundness?: number;
};

export const buildBrandTheme = (input: BrandThemeInput) => {
	const { themeColors, font, darkMode, appComponentOverrides, roundness } =
		input;
	const { LightTheme, DarkTheme } = adaptNavigationTheme({
		reactNavigationLight: NavigationDefaultTheme as unknown as NavigationTheme,
		reactNavigationDark: NavigationDarkTheme as unknown as NavigationTheme,
	});

	const baseTheme = darkMode
		? deepmerge(MD3DarkTheme, DarkTheme)
		: deepmerge(MD3LightTheme, LightTheme);

	const remoteColors =
		(darkMode ? themeColors?.dark : themeColors?.light) ?? {};

	const mergedCustomTheme = deepmerge(baseTheme, {
		colors: { ...remoteColors },
	});

	const customPalette = getCustomPalette(darkMode);
	const customTextColors = textColors(darkMode);
	const resolvedFont = font ?? "opensans";
	const fontConfig = buildFontConfig(resolvedFont);
	const componentOverrides = buildComponentOverrides(appComponentOverrides);

	const colors = {
		white: "#fff",
		black: "#000",
		...mergedCustomTheme.colors,
		text: darkMode ? "#fff" : "#000",
	};

	return {
		...mergedCustomTheme,
		dark: darkMode,
		roundness: roundness ?? MD3LightTheme.roundness,
		colors,
		palette: {
			...customPalette,
			text: {
				...customTextColors,
			},
		},
		breakpoints,
		typography: typography(darkMode, resolvedFont),
		fonts: configureFonts({ config: fontConfig }),
		componentOverrides,
	};
};
